import { useState, useEffect } from 'react';
import { Container, Header, Content, Navbar, Table, Button, Modal, ButtonToolbar, Input, InputGroup, Panel, Message, Tag, TagGroup, IconButton, Tooltip, Whisper, Checkbox } from 'rsuite';
import { Search, Trash, Edit as EditIcon, ArrowDown } from '@rsuite/icons';
import { getApiUrl, getAuthHeaders, getUserId } from './utils/apiConfig';
import { useAuth } from './utils/useAuth';
import DateDisplay from './components/DateDisplay';
import { formatDateTime } from './utils/dateUtils';
import CopyButton from './components/CopyButton';

const { Column, HeaderCell, Cell } = Table;

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return 'N/A';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

const fetchModels = async (getAccessTokenSilently, user) => {
  // Don't try to fetch if getAccessTokenSilently is not available
  if (!getAccessTokenSilently) {
    throw new Error('Authentication required. Auth0 token not available.');
  }
  
  const userId = getUserId(user);
  if (!userId) {
    throw new Error('User ID not found. Please set up your user ID first.');
  }
  
  const headers = await getAuthHeaders(getAccessTokenSilently);
  const response = await fetch(getApiUrl(`/users/${userId}/models`), {
    method: "GET",
    headers: headers,
  });
  
  if (response.status === 401) {
    throw new Error('Unauthorized - Authentication required');
  }
  
  if (!response.ok) {
    throw new Error(`Failed to fetch models: ${response.statusText}`);
  }
  
  const data = await response.json();
  return data;
};

function SavedModels() {
  const auth = useAuth();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedModelIds, setSelectedModelIds] = useState(new Set());
  const [modelDetails, setModelDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [editTagsInput, setEditTagsInput] = useState('');
  const [editDescriptionInput, setEditDescriptionInput] = useState('');
  const [originalDescription, setOriginalDescription] = useState('');
  const [originalTags, setOriginalTags] = useState('');
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  // Trigger refresh when auth becomes ready
  useEffect(() => {
    if (auth.hasAuth0 && !auth.isLoading && auth.isAuthenticated && auth.getAccessTokenSilently) {
      setRefreshKey(prev => prev + 1);
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.hasAuth0]);

  useEffect(() => {
    // Only load models if Auth0 is ready and user is authenticated
    if (auth.hasAuth0 && !auth.isLoading && auth.isAuthenticated && auth.getAccessTokenSilently) {
      loadModels();
    } else if (!auth.hasAuth0) {
      // If Auth0 is not configured, still try to load (might use API key)
      loadModels();
    }
  }, [refreshKey, auth.isLoading, auth.isAuthenticated, auth.hasAuth0]);

  const loadModels = async () => {
    // Don't try to load if Auth0 is still loading or user is not authenticated
    if (auth.hasAuth0 && (auth.isLoading || !auth.isAuthenticated || !auth.getAccessTokenSilently)) {
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const data = await fetchModels(auth.getAccessTokenSilently, auth.user);
      setModels(data.models || []);
    } catch (err) {
      console.error('Error loading models:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (modelIdsToDelete) => {
    if (!modelIdsToDelete || modelIdsToDelete.length === 0) return;
    
    const userId = getUserId(auth.user);
    if (!userId) {
      setError('User ID not found. Please set up your user ID first.');
      return;
    }
    
    setLoading(true);
    try {
      const headers = await getAuthHeaders(auth.getAccessTokenSilently);
      const results = await Promise.allSettled(
        modelIdsToDelete.map(modelId =>
          fetch(getApiUrl(`/users/${userId}/models/${modelId}`), {
            method: 'DELETE',
            headers: headers,
          })
        )
      );
      
      const failed = results.filter(r => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.ok));
      if (failed.length > 0) {
        console.error('Some models failed to delete:', failed);
        setError('Failed to delete some models. Please try again.');
      }
      
      setShowDeleteModal(false);
      setSelectedModelIds(new Set());
      setSelectedModel(null);
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Error deleting models:', error);
      setError(error.message || 'Failed to delete models. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = () => {
    if (selectedModelIds.size === 0) return;
    setShowDeleteModal(true);
  };

  const handleToggleSelect = (modelId) => {
    setSelectedModelIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(modelId)) {
        newSet.delete(modelId);
      } else {
        newSet.add(modelId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (modelsToSelect) => {
    const allSelected = modelsToSelect.every(model => selectedModelIds.has(model.model_id));
    if (allSelected) {
      // Deselect all filtered models
      const newSet = new Set(selectedModelIds);
      modelsToSelect.forEach(model => newSet.delete(model.model_id));
      setSelectedModelIds(newSet);
    } else {
      // Select all filtered models
      const newSet = new Set(selectedModelIds);
      modelsToSelect.forEach(model => newSet.add(model.model_id));
      setSelectedModelIds(newSet);
    }
  };

  const handleViewDetails = async (model) => {
    setSelectedModel(model);
    setShowDetailsModal(true);
    setModelDetails(null);
    setDetailsLoading(true);
    setError(null);
    
    const userId = getUserId(auth.user);
    if (!userId) {
      setModelDetails({ error: 'User ID not found. Please set up your user ID first.' });
      setDetailsLoading(false);
      return;
    }
    
    try {
      const headers = await getAuthHeaders(auth.getAccessTokenSilently);
      const response = await fetch(getApiUrl(`/users/${userId}/models/${model.model_id}`), {
        method: 'GET',
        headers: headers,
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch model details');
      }
      
      const data = await response.json();
      setModelDetails(data);
      const description = data.description || '';
      const tags = (data.tags || []).join(', ');
      setEditDescriptionInput(description);
      setEditTagsInput(tags);
      setOriginalDescription(description);
      setOriginalTags(tags);
    } catch (error) {
      console.error('Error fetching model details:', error);
      setModelDetails({ error: error.message });
    } finally {
      setDetailsLoading(false);
    }
  };

  const hasChanges = () => {
    if (!modelDetails) return false;
    const currentTags = editTagsInput.split(',').map(t => t.trim()).filter(t => t.length > 0).join(', ');
    const originalTagsNormalized = originalTags.split(',').map(t => t.trim()).filter(t => t.length > 0).join(', ');
    return editDescriptionInput !== originalDescription || currentTags !== originalTagsNormalized;
  };

  const handleSave = async () => {
    if (!selectedModel || !modelDetails) return;
    
    // Validate description length
    if (editDescriptionInput.length > 2048) {
      setError('Description exceeds maximum length of 2048 characters');
      return;
    }
    
    // Parse tags from comma-separated string
    const tags = editTagsInput
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);
    
    // Validate tags
    if (tags.length > 256) {
      setError('Maximum number of tags is 256');
      return;
    }
    
    for (const tag of tags) {
      if (tag.length > 256) {
        setError(`Tag "${tag.substring(0, 50)}${tag.length > 50 ? '...' : ''}" exceeds maximum length of 256 characters`);
        return;
      }
    }
    
    const userId = getUserId(auth.user);
    if (!userId) {
      setError('User ID not found. Please set up your user ID first.');
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const authHeaders = await getAuthHeaders(auth.getAccessTokenSilently);
      const response = await fetch(getApiUrl(`/users/${userId}/models/${selectedModel.model_id}`), {
        method: 'PUT',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          description: editDescriptionInput,
          tags: tags
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update model');
      }
      
      const result = await response.json();
      setRefreshKey(prev => prev + 1);
      // Update original values to reflect saved state
      setOriginalDescription(editDescriptionInput);
      setOriginalTags(editTagsInput);
      // Reload details to show updated values
      await handleViewDetails(selectedModel);
    } catch (error) {
      console.error('Error updating model:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (model, fileType = 'model') => {
    const userId = getUserId(auth.user);
    if (!userId) {
      setError('User ID not found. Please set up your user ID first.');
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders(auth.getAccessTokenSilently);
      const response = await fetch(
        getApiUrl(`/users/${userId}/models/${model.model_id}/download?file_type=${fileType}`),
        {
          method: 'GET',
          headers: headers,
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to get download URL');
      }
      
      const data = await response.json();
      
      // Open the download URL in a new window/tab to trigger download
      window.open(data.download_url, '_blank');
    } catch (error) {
      console.error('Error downloading model:', error);
      setError(error.message || 'Failed to download model. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredModels = models.filter(model => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const tagsStr = (model.tags || []).join(' ').toLowerCase();
    return (
      model.model_id?.toLowerCase().includes(searchLower) ||
      model.description?.toLowerCase().includes(searchLower) ||
      tagsStr.includes(searchLower)
    );
  });

  return (
    <Container>
      <Header style={{ position: 'sticky', top: 0, zIndex: 1000 }}>
        <Navbar style={{ display: 'flex', alignItems: 'center' }}>
          <Navbar.Brand>
            Saved Models
          </Navbar.Brand>
          <div style={{ marginLeft: 'auto', marginRight: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <InputGroup inside style={{ width: 300 }}>
              <Input
                placeholder="Search models..."
                value={searchTerm}
                onChange={(value) => setSearchTerm(value)}
              />
              <InputGroup.Addon>
                <Search />
              </InputGroup.Addon>
            </InputGroup>
            {selectedModelIds.size > 0 && (
              <Button 
                appearance="primary" 
                color="red" 
                onClick={handleBulkDelete}
                icon={<Trash />}
              >
                Remove ({selectedModelIds.size})
              </Button>
            )}
            <Button appearance="primary" onClick={loadModels} loading={loading}>
              Refresh
            </Button>
          </div>
        </Navbar>
      </Header>
      <Content>
        <div style={{ padding: '20px' }}>
          <Panel>

          {error && (
            <Message type="error" closable onClose={() => setError(null)}>
              {error}
            </Message>
          )}

          <Table
            data={filteredModels}
            autoHeight
            rowHeight={60}
            loading={loading}
          >
              <Column width={50}>
                <HeaderCell>
                  <Checkbox
                    checked={filteredModels.length > 0 && filteredModels.every(model => selectedModelIds.has(model.model_id))}
                    indeterminate={filteredModels.some(model => selectedModelIds.has(model.model_id)) && !filteredModels.every(model => selectedModelIds.has(model.model_id))}
                    onChange={() => handleSelectAll(filteredModels)}
                  />
                </HeaderCell>
                <Cell>
                  {(rowData) => (
                    <Checkbox
                      checked={selectedModelIds.has(rowData.model_id)}
                      onChange={() => handleToggleSelect(rowData.model_id)}
                    />
                  )}
                </Cell>
              </Column>
              <Column width={120} fixed>
                <HeaderCell>Model ID</HeaderCell>
                <Cell>
                  {(rowData) => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>{rowData.model_id}</span>
                      <div onClick={(e) => e.stopPropagation()}>
                        <CopyButton
                          text={rowData.model_id}
                          size="sm"
                          successMessage="Model ID copied to clipboard"
                          errorMessage="Failed to copy Model ID to clipboard"
                        />
                      </div>
                    </div>
                  )}
                </Cell>
              </Column>

              <Column flexGrow={1} minWidth={200}>
                <HeaderCell>Description</HeaderCell>
                <Cell>
                  {(rowData) => (
                    <span>{rowData.description || <em style={{ color: '#999' }}>No description</em>}</span>
                  )}
                </Cell>
              </Column>

              <Column flexGrow={1} minWidth={200}>
                <HeaderCell>Tags</HeaderCell>
                <Cell>
                  {(rowData) => (
                    rowData.tags && rowData.tags.length > 0 ? (
                      <TagGroup>
                        {rowData.tags.map((tag, idx) => (
                          <Tag key={idx}>{tag}</Tag>
                        ))}
                      </TagGroup>
                    ) : (
                      <em style={{ color: '#999' }}>No tags</em>
                    )
                  )}
                </Cell>
              </Column>

              <Column width={120}>
                <HeaderCell>Size</HeaderCell>
                <Cell>
                  {(rowData) => formatBytes(rowData.total_size_bytes)}
                </Cell>
              </Column>

              <Column width={180}>
                <HeaderCell>Saved At</HeaderCell>
                <Cell>
                  {(rowData) => <DateDisplay isoString={rowData.saved_at} />}
                </Cell>
              </Column>

              <Column width={150} flexGrow={1} fixed="right">
                <HeaderCell>Actions</HeaderCell>
                <Cell>
                  {(rowData) => (
                    <ButtonToolbar style={{ whiteSpace: 'nowrap' }}>
                      <Whisper
                        placement="top"
                        trigger="hover"
                        speaker={<Tooltip>View details</Tooltip>}
                      >
                        <IconButton
                          icon={<EditIcon />}
                          color="blue"
                          appearance="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(rowData);
                          }}
                        />
                      </Whisper>
                      <Whisper
                        placement="top"
                        trigger="hover"
                        speaker={<Tooltip>Download model</Tooltip>}
                      >
                        <IconButton
                          icon={<ArrowDown />}
                          color="green"
                          appearance="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(rowData, 'model');
                          }}
                        />
                      </Whisper>
                      <Whisper
                        placement="top"
                        trigger="hover"
                        speaker={<Tooltip>Remove model</Tooltip>}
                      >
                        <IconButton
                          icon={<Trash />}
                          color="red"
                          appearance="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedModel(rowData);
                            setShowDeleteModal(true);
                          }}
                        />
                      </Whisper>
                    </ButtonToolbar>
                  )}
                </Cell>
              </Column>
            </Table>

          {filteredModels.length > 0 && (
            <div style={{ marginTop: 10, color: '#999', fontSize: '0.9em' }}>
              Showing {filteredModels.length} of {models.length} model{models.length !== 1 ? 's' : ''}
            </div>
          )}
          </Panel>

          {/* Details Modal */}
          <Modal 
            open={showDetailsModal} 
            onClose={() => {
              if (hasChanges()) {
                if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
                  setShowDetailsModal(false);
                  setError(null);
                }
              } else {
                setShowDetailsModal(false);
                setError(null);
              }
            }}
            size="lg"
          >
            <Modal.Header>
              <Modal.Title>Model Details: {selectedModel?.model_id}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {detailsLoading ? (
                <div>Loading details...</div>
              ) : modelDetails?.error ? (
                <div style={{ color: 'red' }}>Error: {modelDetails.error}</div>
              ) : modelDetails ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  {error && (
                    <Message type="error" closable onClose={() => setError(null)}>
                      {error}
                    </Message>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <strong>Model ID:</strong> {modelDetails.model_id}
                    <CopyButton
                      text={modelDetails.model_id}
                      size="sm"
                      successMessage="Model ID copied to clipboard"
                      errorMessage="Failed to copy Model ID to clipboard"
                    />
                  </div>
                  <div>
                    <strong>Description:</strong>
                    <div style={{ marginTop: '5px' }}>
                      <Input
                        as="textarea"
                        rows={3}
                        value={editDescriptionInput}
                        onChange={setEditDescriptionInput}
                        placeholder="Enter model description..."
                        maxLength={2048}
                      />
                      <div style={{ marginTop: 5, fontSize: '0.85em', color: '#999' }}>
                        {editDescriptionInput.length} / 2048 characters
                      </div>
                    </div>
                  </div>
                  <div>
                    <strong>Tags:</strong>
                    <div style={{ marginTop: '5px' }}>
                      <Input
                        value={editTagsInput}
                        onChange={setEditTagsInput}
                        placeholder="e.g., production, resnet50, imagenet"
                      />
                      <div style={{ marginTop: 5, fontSize: '0.85em', color: '#999' }}>
                        Enter tags separated by commas. Max 256 tags, 256 characters per tag.
                      </div>
                      <div style={{ marginTop: 5, fontSize: '0.85em', color: '#999' }}>
                        Current: {editTagsInput.split(',').filter(t => t.trim().length > 0).length} tag(s)
                      </div>
                    </div>
                  </div>
                  <div>
                    <strong>Size:</strong> {formatBytes(modelDetails.total_size_bytes)}
                  </div>
                  <div>
                    <strong>Saved At:</strong> {formatDateTime(modelDetails.saved_at)}
                  </div>
                </div>
              ) : null}
            </Modal.Body>
            <Modal.Footer style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', whiteSpace: 'nowrap' }}>
              {hasChanges() ? (
                <>
                  <Button 
                    onClick={() => {
                      // Reset to original values
                      setEditDescriptionInput(originalDescription);
                      setEditTagsInput(originalTags);
                      setError(null);
                    }} 
                    appearance="subtle"
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSave} appearance="primary" loading={loading}>
                    Save
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    icon={<ArrowDown />}
                    onClick={() => {
                      if (selectedModel) {
                        handleDownload(selectedModel, 'model');
                      }
                    }}
                    appearance="primary"
                    loading={loading}
                  >
                    Download Model
                  </Button>
                  <Button 
                    onClick={() => {
                      setShowDetailsModal(false);
                      setError(null);
                    }} 
                    appearance="subtle"
                  >
                    Close
                  </Button>
                </>
              )}
            </Modal.Footer>
          </Modal>

          {/* Remove Confirmation Modal */}
        <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)} size="sm">
          <Modal.Header>
            <Modal.Title>Remove Model{selectedModelIds.size > 1 ? 's' : ''}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedModelIds.size > 0 ? (
              <p>
                Are you sure you want to remove {selectedModelIds.size} selected model{selectedModelIds.size !== 1 ? 's' : ''}?
              </p>
            ) : (
              <>
                <p>
                  Are you sure you want to remove the model <strong>{selectedModel?.model_id}</strong>?
                </p>
                {selectedModel?.description && (
                  <p style={{ marginTop: 10 }}>
                    <strong>Description:</strong> {selectedModel.description}
                  </p>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={() => setShowDeleteModal(false)} appearance="subtle">
              Cancel
            </Button>
            <Button 
              onClick={() => {
                if (selectedModelIds.size > 0) {
                  handleDelete(Array.from(selectedModelIds));
                } else if (selectedModel) {
                  handleDelete([selectedModel.model_id]);
                }
              }} 
              color="red" 
              appearance="primary" 
              loading={loading}
            >
              Remove
            </Button>
          </Modal.Footer>
        </Modal>
        </div>
      </Content>
    </Container>
  );
}

export default SavedModels;


